import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowsClockwise, Download, IdentificationCard, ShareNetwork } from '@phosphor-icons/react';
import QRCode from 'qrcode';
import { issuePass, renderCard, rotatePass, type CardRender } from '@/api/membercards';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/LoadError';
import { MemberCardSkeleton } from '@/components/ui/member-card-skeleton';
import { useConfirm } from '@/components/ConfirmDialog';

// Member card view at /orgs/:slug/members/:memberID/card. Shows the
// rendered card with the QR painted client-side, plus PNG / PDF download
// buttons that derive locally from the SVG. The pass's public URL is
// the QR payload — the QR resolves to the no-auth scan page on
// /m/c/:token regardless of which subdomain it's printed on.
export function MemberCard() {
  const { slug = '', memberID = '' } = useParams<{ slug: string; memberID: string }>();
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const { t, i18n } = useTranslation('common');
  const qc = useQueryClient();
  const confirm = useConfirm();

  const renderQuery = useQuery<CardRender>({
    queryKey: ['membercard-render', slug, memberID],
    queryFn: () => renderCard(token, slug, memberID),
    enabled: !!slug && !!memberID,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });

  const issueMut = useMutation({
    mutationFn: () => issuePass(token, slug, memberID),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['membercard-render', slug, memberID] }),
  });

  const rotateMut = useMutation({
    mutationFn: () => rotatePass(token, slug, memberID),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['membercard-render', slug, memberID] }),
  });

  if (renderQuery.isPending) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <MemberCardSkeleton />
      </div>
    );
  }
  if (renderQuery.error instanceof ApiError && renderQuery.error.status === 404) {
    return (
      <div className="p-6 max-w-xl">
        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <IdentificationCard size={24} weight="bold" />
          {t('memberCards.passHeading')}
        </h1>
        <p className="text-sm text-muted-foreground mb-4">{t('memberCards.passEmpty')}</p>
        <Button onClick={() => issueMut.mutate()} disabled={issueMut.isPending}>
          {t('memberCards.issueCta')}
        </Button>
      </div>
    );
  }
  if (renderQuery.error) {
    return <LoadError onRetry={() => void renderQuery.refetch()} />;
  }

  const data = renderQuery.data!;
  const dateFmt = new Intl.DateTimeFormat(i18n.language || 'en', { dateStyle: 'medium' });
  const paidAt = data.status.paid_at ? dateFmt.format(new Date(data.status.paid_at)) : null;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <IdentificationCard size={24} weight="bold" />
        {t('memberCards.passHeading')}
      </h1>

      <div className="rounded-2xl border p-5 mb-4">
        <CardCanvas render={data} />

        <dl className="grid sm:grid-cols-2 gap-3 mt-5 text-sm">
          <div>
            <dt className="text-muted-foreground">{t('memberCards.publicPaid', { year: data.status.fiscal_year }).split(' ').slice(0, 2).join(' ')}</dt>
            <dd className="font-bold">
              {data.status.paid
                ? t('memberCards.publicPaid', { year: data.status.fiscal_year })
                : t('memberCards.publicUnpaid', { year: data.status.fiscal_year })}
            </dd>
            {paidAt ? <dd className="text-xs text-muted-foreground">{paidAt}</dd> : null}
          </div>
          <div>
            <dt className="text-muted-foreground">{t('memberCards.scanHint')}</dt>
            <dd className="text-xs font-mono break-all">{data.pass.public_url}</dd>
          </div>
        </dl>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => downloadPNG(data)}>
          <Download size={18} weight="bold" className="mr-1" />
          {t('memberCards.downloadPng')}
        </Button>
        <Button variant="outline" onClick={() => downloadPDF(data)}>
          <Download size={18} weight="bold" className="mr-1" />
          {t('memberCards.downloadPdf')}
        </Button>
        <Button
          variant="outline"
          onClick={async () => {
            const ok = await confirm({
              title: t('memberCards.rotateCta'),
              body: t('memberCards.scanHint'),
              confirmLabel: t('memberCards.rotateCta'),
            });
            if (ok) rotateMut.mutate();
          }}
        >
          <ArrowsClockwise size={18} weight="bold" className="mr-1" />
          {t('memberCards.rotateCta')}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            if (navigator.share) {
              void navigator.share({ url: data.pass.public_url, title: t('memberCards.title') });
            } else {
              void navigator.clipboard?.writeText(data.pass.public_url);
            }
          }}
        >
          <ShareNetwork size={18} weight="bold" className="mr-1" />
          {t('memberCards.title')}
        </Button>
      </div>
    </div>
  );
}

function CardCanvas({ render }: { render: CardRender }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Paint the SVG into the container, then replace any data-kind="qr"
  // placeholder with a real QR. The server emits a placeholder rect with
  // data-url + data-size attributes so this client step is purely
  // ornamental (the URL itself doesn't change).
  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = render.svg;
    const qrSlot = containerRef.current.querySelector('g[data-kind="qr"]');
    if (qrSlot) {
      const url = qrSlot.getAttribute('data-url') ?? render.pass.public_url;
      const size = parseInt(qrSlot.getAttribute('data-size') ?? '160', 10);
      const x = parseInt(qrSlot.getAttribute('data-x') ?? '0', 10);
      const y = parseInt(qrSlot.getAttribute('data-y') ?? '0', 10);
      void QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 1, scale: 6 }).then((dataURL) => {
        const img = document.createElementNS('http://www.w3.org/2000/svg', 'image');
        img.setAttribute('href', dataURL);
        img.setAttribute('x', String(x));
        img.setAttribute('y', String(y));
        img.setAttribute('width', String(size));
        img.setAttribute('height', String(size));
        qrSlot.replaceWith(img);
      });
    }
  }, [render]);

  return <div ref={containerRef} className="rounded-xl overflow-hidden shadow-2xl" style={{ aspectRatio: '856 / 540' }} />;
}

// downloadPNG rasterizes the rendered SVG via canvas.drawImage and
// triggers a save. Keeps the PNG path entirely client-side — no extra
// server round-trip, no chromedp dependency for the lightweight format.
async function downloadPNG(render: CardRender) {
  const dataURL = await rasterize(render.svg, 'image/png', 2);
  triggerDownload(dataURL, `membercard-${render.pass.id}.png`);
}

async function downloadPDF(render: CardRender) {
  // Minimal client-side PDF: open a print window with the SVG sized for
  // CR80 (a common membership-card size). Browser print-to-PDF works
  // everywhere without a PDF library bundle.
  const win = window.open('', '_blank', 'noopener,noreferrer,width=900,height=600');
  if (!win) return;
  win.document.write(
    `<!DOCTYPE html><html><head><title>Member card</title><style>
      @page { size: 85.6mm 54mm; margin: 0; }
      body { margin: 0; display: flex; align-items: center; justify-content: center; }
      svg { width: 100%; height: auto; max-width: 856px; }
    </style></head><body>${render.svg}</body></html>`,
  );
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}

async function rasterize(svgText: string, mime: string, scale: number): Promise<string> {
  const blob = new Blob([svgText], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = url;
    });
    const w = (img.naturalWidth || 856) * scale;
    const h = (img.naturalHeight || 540) * scale;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL(mime);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function triggerDownload(dataURL: string, name: string) {
  const a = document.createElement('a');
  a.href = dataURL;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
